/**
 * Listen to Novu realtime events and surface them as in-app toast notifications.
 *
 * Data flow:
 * - Subscribe to `notifications.notification_received` on the Novu client.
 * - Normalize the event payload into a stable dedupe key.
 * - Render a `react-toastify` toast using the best available title/body fields.
 *
 * Notes:
 * - This component renders `null` and exists purely for side effects.
 * - A bounded "seen set" is used to prevent duplicate toasts when the same event is replayed.
 */
import React, { useEffect, useLayoutEffect, useRef } from 'react';
import { useNovu } from '@novu/react';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { getNotificationText } from './notificationI18n';

const MAX_SEEN = 200;

/**
 * Build a stable key for a notification so we can dedupe toasts across event replays.
 *
 * We prefer IDs when present, otherwise fall back to transactionId, then createdAt.
 * @param {object} notification
 * @returns {string|null}
 */
function getNotificationKey(notification) {
  const id = notification?.id || notification?._id;
  if (id) return String(id);
  const transactionId = notification?.transactionId;
  if (transactionId) return `tx:${transactionId}`;
  const createdAt = notification?.createdAt;
  if (createdAt) return `ts:${createdAt}`;
  return null;
}

/**
 * Hook-only component that subscribes to Novu events and displays toasts.
 * @returns {null}
 */
export default function NovuRealtimeToasts() {
  const novu = useNovu();
  const { t, i18n } = useTranslation('global');
  const language = i18n?.language;
  const textContextRef = useRef(null);
  // Translation changes do not replace the subscription or cancel its queue.
  // Only committed UI language may format a notification when it is displayed.
  useLayoutEffect(() => {
    // react-i18next's t may consult mutable engine language. Pin every lookup,
    // including helper interpolation and batch copy, to this committed locale.
    const translate = (key, options) => t(key, {
      ...options, lng: language, lngs: undefined, fallbackLng: undefined,
    });
    textContextRef.current = { t: translate, language };
  }, [t, language]);

  const seenKeysRef = useRef([]);
  const seenSetRef = useRef(new Set());
  const pendingRef = useRef([]);
  const flushTimerRef = useRef(null);

  useEffect(() => {
    if (!novu || typeof novu.on !== 'function') return;
    // Each subscription has its own lease. StrictMode or a new SDK island
    // must not reactivate a retained callback from the previous effect.
    let active = true;
    const seenSet = seenSetRef.current;

    const flushPendingToasts = () => {
      if (!active) return;
      flushTimerRef.current = null;
      const queued = pendingRef.current;
      pendingRef.current = [];
      if (!queued.length) return;
      const { t: currentT, language: currentLanguage } = textContextRef.current;
      const pending = queued.map(({ key, notification }) => {
        const { title, body } = getNotificationText(notification, currentT, currentLanguage);
        return { key, title, body };
      });

      // Group bursts of notifications by title to avoid spam when many notifications arrive at once
      // (e.g. booking multiple lessons in one action or backlog replay on reconnect).
      const groups = new Map();
      for (const item of pending) {
        const groupKey = String(item?.title || currentT('notifications.toast.title'));
        const list = groups.get(groupKey);
        if (list) list.push(item);
        else groups.set(groupKey, [item]);
      }

      for (const [groupTitle, items] of groups.entries()) {
        if (items.length === 1) {
          const it = items[0];
          toast.info(
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <div style={{ fontWeight: 700, lineHeight: 1.2 }}>{it.title}</div>
              {it.body ? <div style={{ opacity: 0.9, lineHeight: 1.25 }}>{it.body}</div> : null}
            </div>,
            {
              toastId: `novu:${it.key}`,
              autoClose: 8000,
              pauseOnHover: true,
              closeOnClick: true,
            }
          );
          continue;
        }

        const MAX_LINES = 3;
        const lines = items
          .map((it) => String(it?.body || '').trim())
          .filter(Boolean);
        const shown = lines.slice(0, MAX_LINES);
        const remaining = Math.max(0, items.length - shown.length);

        const summaryTitle = `${groupTitle} (${items.length})`;
        const toastId = `novu:batch:${items.map((it) => it.key).join(',')}`;

        toast.info(
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <div style={{ fontWeight: 700, lineHeight: 1.2 }}>{summaryTitle}</div>
            {shown.length ? (
              <div style={{ opacity: 0.9, lineHeight: 1.25 }}>
                {shown.map((line, idx) => (
                  <div key={`${toastId}:${idx}`}>{line}</div>
                ))}
                {remaining > 0 ? (
                  <div style={{ opacity: 0.85, marginTop: 4 }}>
                    {currentT('notifications.toast.andMore', {
                      count: remaining,
                      defaultValue: `+${remaining} more`,
                    })}
                  </div>
                ) : null}
              </div>
            ) : null}
          </div>,
          {
            toastId,
            autoClose: 9000,
            pauseOnHover: true,
            closeOnClick: true,
          }
        );
      }
    };

    const scheduleFlush = () => {
      if (flushTimerRef.current) return;
      // Small delay so bursts can be grouped into a single toast.
      flushTimerRef.current = setTimeout(flushPendingToasts, 150);
    };

    const cleanup = novu.on('notifications.notification_received', ({ result }) => {
      if (!active) return;
      const notification = result;
      const key = getNotificationKey(notification);
      if (!key) return;

      // Prevent spamming duplicates if the same notification is emitted multiple times.
      if (seenSet.has(key)) return;
      seenSet.add(key);
      seenKeysRef.current.push(key);
      if (seenKeysRef.current.length > MAX_SEEN) {
        const old = seenKeysRef.current.shift();
        if (old) seenSet.delete(old);
      }

      pendingRef.current.push({ key, notification });
      scheduleFlush();
    });

    return () => {
      active = false;
      try {
        if (typeof cleanup === 'function') cleanup();
      } catch (_) {
        // ignore
      }
      try {
        if (flushTimerRef.current) {
          clearTimeout(flushTimerRef.current);
          flushTimerRef.current = null;
        }
      } catch (_) {
        // ignore
      }
      // Cancelled items were never displayed. Allow a later live subscription
      // to redeliver only those keys, preserving already-displayed history.
      const discardedKeys = new Set(pendingRef.current.map(item => item.key));
      if (discardedKeys.size) {
        seenKeysRef.current = seenKeysRef.current.filter(key => !discardedKeys.has(key));
        discardedKeys.forEach(key => seenSet.delete(key));
      }
      pendingRef.current = [];
    };
  }, [novu]);

  return null;
}
